const INFO = {
	main: {
		title: "Bijan Saud",
		name: "Bijan Saud",
		email: "bijansaud@gmail.com",
		location: "Dallas, TX",
		logo: "../logo.png",
		role: "Developer. Security Engineer. Entrepreneur. Poet.",
		tagline:
			"I spend my days finding holes in enterprise software and my nights building games, SaaS platforms, and whatever else I can't get out of my head. Based in Dallas.",
	},

	socials: {
		twitter: "https://twitter.com/saud_bijan",
		github: "https://github.com/BijanSaud1",
		linkedin: "https://www.linkedin.com/in/bijan-saud-013838195/",
		instagram: "https://www.instagram.com/bloodbijan/",
		poetryInstagram: "https://www.instagram.com/poetrypin__ts/",
		facebook: "https://facebook.com/",
	},

	about: {
		title: "Who I am",
		description:
			"I'm a developer who got really into security. Now I do both. During the day I work at Elevance Health — finding and fixing vulnerabilities across enterprise apps. On my own time I build things. A medieval god-sim in Unity, a SaaS platform in Next.js, a research project on AI agent security. I like building stuff that works and breaking stuff that shouldn't.",
		whatIDo: [
			{
				title: "Security",
				description: "I find vulnerabilities in enterprise apps using Checkmarx, Qualys, and Burp Suite — then work with dev teams to fix them.",
				icon: "faShieldHalved",
			},
			{
				title: "Development",
				description: "React, Next.js, Spring Boot, Unity. I build full-stack apps and games. I ship things.",
				icon: "faCode",
			},
			{
				title: "DevSecOps",
				description: "CI/CD pipelines, Terraform, Docker, AWS. I automate security into the pipeline so it's not an afterthought.",
				icon: "faCloud",
			},
			{
				title: "Research",
				description: "Currently researching how to detect attacks in AI agent systems. Building detection frameworks, chasing a publication.",
				icon: "faFlask",
			},
			{
				title: "Entrepreneurship",
				description: "Building StudioStead — a SaaS platform for small businesses. I don't just write code, I build products.",
				icon: "faRocket",
			},
		],
	},

	poetry: [
		{
			title: "The Code We Leave Behind",
			lines: [
				"We write in silence, line by line,",
				"A logic built, a grand design.",
				"But what remains when screens go dark?",
				"The human soul — the truest mark.",
			],
		},
		{
			title: "Between the Brackets",
			lines: [
				"Between the brackets, worlds are born,",
				"Of midnight oil and coffee worn.",
				"Each function holds a quiet prayer —",
				"That something works, that someone's there.",
			],
		},
		{
			title: "Zero Day",
			lines: [
				"They search for cracks in walls we build,",
				"For doors left open, vaults unfilled.",
				"We patch, we harden, stand our ground —",
				"In silence, where the threats are found.",
			],
		},
	],

	projects: [
		{
			title: "SimulateWorld",
			description:
				"A 3D medieval god-simulator where you shape the world from above — then drop in and live in it as a character. 20+ interconnected systems running at once: economy, combat, crime, religion, weather, politics. Nothing is scripted. Everything emerges from the mechanics.",
			tags: [
				"Unity 6", "C#", "URP", "Utility AI", "Procedural Generation",
			],
			link: "",
			linkText: "In Development",
			status: "in-progress",
			progress: null,
			highlight: true,
			stats: null,
			features: [
				"Seamless switching between God Mode and Character Mode",
				"Procedural voxel terrain with biomes and rivers",
				"NPC perception, memory, mood, and gossip",
				"Feudal economy with supply, demand, and famine",
				"Crime, evidence, trials, and punishment",
				"Melee combat with wounds, weapons, and armies",
				"Dynamic weather affecting crops, movement, and disease",
				"Generational dynasty play with ruins of past civilizations",
			],
		},
		{
			title: "StudioStead",
			description:
				"A SaaS platform for small businesses and creators. Appointment scheduling, product sales, drag-and-drop website builder, staff management, marketing tools, payments. I built the whole thing solo — architecture to deployment.",
			tags: [
				"Next.js 16", "React 19", "TypeScript", "Supabase",
				"Stripe Connect", "Tailwind CSS", "AI Integration",
			],
			link: "",
			linkText: "In Development",
			status: "in-progress",
			progress: null,
			highlight: true,
			stats: null,
			features: [
				"Multi-tenant architecture with custom domains",
				"Booking system with availability and calendar views",
				"Storefront with cart, checkout, and order tracking",
				"Drag-and-drop website builder with themes",
				"AI-powered content generation",
				"Stripe Connect for merchant payments",
				"Email and SMS notifications",
				"Admin dashboard with analytics and staff roles",
			],
		},
		{
			title: "FC Ultimate Team Companion",
			description:
				"I got tired of using five different sites to check prices, build squads, and track the market. So I'm building my own — squad builder, live market tracking, price alerts, player comparisons, SBC solutions. All in one place.",
			tags: [
				"React", "Node.js", "EA FC API", "Real-time Data",
			],
			link: "",
			linkText: "In Development",
			status: "in-progress",
			progress: null,
			highlight: true,
			stats: null,
			features: [
				"Squad builder with chemistry and rating calculations",
				"Live market price tracking and alerts",
				"Player comparison with stats breakdown",
				"SBC solution finder",
				"Investment tracker for trading",
				"Meta analysis for current patch",
			],
		},
	],

	upcomingProjects: [],

	research: [
		{
			title: "WEAVE",
			subtitle: "Active Research — 2026 – Present",
			description:
				"AI agents are starting to talk to each other through something called the Model Context Protocol. The problem is nobody's really figured out how to tell when one of those agents is doing something it shouldn't be. That's what I'm working on — a way to detect when an AI agent goes off-script and starts messing with servers it's not supposed to touch. I'm building detection tools, a benchmark to test them, and a trust-scoring system so you can actually tell which MCP servers are safe. Trying to get this published.",
			tags: [
				"Python", "MCP SDK", "PM4Py", "DoWhy", "PyTorch", "LaTeX",
			],
			link: "",
			components: [
				"Workflow declaration language for AI agents",
				"Four-layer behavioral detection framework",
				"Conformance & statistical analysis",
				"Sequence modeling & causal inference",
				"Public benchmark dataset for MCP security",
				"Trust-scoring system for MCP servers",
				"Process mining for agent behavior",
				"Targeting arXiv preprint & workshop publication",
			],
		},
	],

	interests: [
		{
			title: "Security Research",
			description:
				"I keep up with new CVEs, zero-days, and attack vectors. I read threat intel reports and security papers regularly — it's how I stay sharp.",
			icon: "faShieldHalved",
		},
		{
			title: "Dev & Tech Trends",
			description:
				"I follow what's new in frameworks, languages, and AI. I try new stacks when they come out and see what's worth using.",
			icon: "faCode",
		},
		{
			title: "Hacking & CTFs",
			description:
				"Hack The Box, TryHackMe, CTF competitions. Privilege escalation, web exploitation, AD attacks. I break things to learn how they work.",
			icon: "faTerminal",
		},
		{
			title: "Game Development",
			description:
				"I'm building SimulateWorld in Unity 6. 20+ systems, procedural world generation, utility AI. This is where code meets creativity for me.",
			icon: "faCube",
		},
		{
			title: "Gaming",
			description:
				"Strategy games, RPGs, anything with deep systems. Playing games is how I got into building them.",
			icon: "faGamepad",
		},
		{
			title: "FC Ultimate Team",
			description:
				"I play FC Ultimate Team on PC. If you think you're good, challenge me. Seriously.",
			icon: "faFutbol",
		},
		{
			title: "Working Out",
			description:
				"Got into lifting recently. It's the one part of my day where I'm not at a screen and my brain actually shuts up for a bit.",
			icon: "faDumbbell",
		},
		{
			title: "Hiking",
			description:
				"Started hitting trails around Dallas. Nothing resets your head like being outside with no signal.",
			icon: "faPersonHiking",
		},
	],

	experience: [
		{
			company: "Elevance Health",
			role: "Application Security Developer",
			duration: "May 2024 - Present",
			location: "Grand Prairie, TX",
			logo: "./utawork.png",
			description:
				"I work on vulnerability management across enterprise Java/J2EE applications. I use Checkmarx, Qualys, Burp Suite, and Wiz to find security vulnerabilities, then work with dev and DevOps teams to get them fixed. I also build and secure REST APIs with OAuth2/JWT, enforce MFA and RBAC, and automate security with AWS, Terraform, Docker, and Jenkins.",
			environment: [
				"Checkmarx", "Qualys", "Burp Suite", "Jenkins", "Wiz",
				"Terraform", "AWS", "Docker", "Oracle SQL", "GitHub",
			],
		},
	],

	certifications: [
		{
			name: "CompTIA Security+",
			status: "In Progress",
		},
		{
			name: "AWS Certified Developer – Associate",
			status: "In Progress",
		},
		{
			name: "Checkmarx Secure Coding Fundamentals",
			status: "Completed",
		},
		{
			name: "Hack The Box Academy",
			status: "Completed",
			details: [
				"SOC Analyst Path",
				"Active Directory Enumeration & Attack Mitigation",
				"Web Attacks and Exploitation",
				"Privilege Escalation and Post-Exploitation",
				"Penetration Testing & Vulnerability Management",
			],
		},
	],

	skills: {
		"Identity & Access Management": [
			{ name: "SSO / MFA", level: "Highly Proficient" },
			{ name: "OAuth2 / OpenID Connect", level: "Highly Proficient" },
			{ name: "Active Directory", level: "Proficient" },
			{ name: "PAM / Provisioning", level: "Proficient" },
			{ name: "Identity Analytics", level: "Proficient" },
		],
		"Cyber Defense": [
			{ name: "SIEM / EDR Monitoring", level: "Proficient" },
			{ name: "Firewall & IDS/IPS", level: "Proficient" },
			{ name: "SOC Alert Triage", level: "Proficient" },
			{ name: "Incident Response", level: "Proficient" },
			{ name: "Threat Detection", level: "Proficient" },
		],
		"Vulnerability Management": [
			{ name: "Checkmarx", level: "Highly Proficient" },
			{ name: "IBM AppScan", level: "Proficient" },
			{ name: "Qualys", level: "Proficient" },
			{ name: "OWASP Dependency-Check", level: "Proficient" },
			{ name: "Burp Suite", level: "Proficient" },
		],
		"Development & Automation": [
			{ name: "Java / Spring Boot", level: "Highly Proficient" },
			{ name: "React / Next.js", level: "Highly Proficient" },
			{ name: "TypeScript", level: "Proficient" },
			{ name: "Python", level: "Proficient" },
			{ name: "C# / Unity", level: "Highly Proficient" },
			{ name: "Node.js", level: "Proficient" },
			{ name: "Jenkins CI/CD", level: "Proficient" },
			{ name: "Terraform / IaC", level: "Proficient" },
			{ name: "Docker", level: "Proficient" },
		],
		"Cloud & Infrastructure": [
			{ name: "AWS (IAM, EC2, S3, VPC)", level: "Proficient" },
			{ name: "Firebase", level: "Proficient" },
			{ name: "Supabase", level: "Beginner" },
			{ name: "CloudTrail / Monitoring", level: "Proficient" },
			{ name: "Kubernetes", level: "Beginner" },
			{ name: "Network Segmentation", level: "Proficient" },
		],
		concepts: [
			"NIST CSF",
			"HIPAA",
			"FERPA",
			"TAC 202",
			"Zero-Trust Architecture",
			"Secure SDLC",
			"REST API Design",
			"Design Patterns",
			"MVC Architecture",
			"Agile / Scrum",
		],
	},
};

export default INFO;
